import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import Select from "react-select";
import { FaPlus, FaTimes, FaTruck, FaTrashAlt } from "react-icons/fa";
import { HiOutlineTrash } from "react-icons/hi";

const style = {
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  control: (provided) => ({
    ...provided,
    minHeight: "28px",
    height: "28px",
    borderRadius: "6px",
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "28px",
    padding: "0 4px",
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "28px",
  }),
};

const TransferInventoryModal = ({ onClose, onTransfer }) => {
  const [transferItems, setTransferItems] = useState([
    {
      itemCode: "",
      itemName: "",
      itemCategory: "",
      itemImage: "",
      transferQuantity: 0,
      inwardReference: "",
      inwardShipmentMark: "NA",
    },
  ]);
  const [fromWarehouseName, setFromWarehouseName] = useState(null);
  const [toWarehouseName, setToWarehouseName] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [materialCheckedBy, setMaterialCheckedBy] = useState("");
  const [driverName, setDriverName] = useState("");
  const [driverMobileNumber, setDriverMobileNumber] = useState("");

  useEffect(() => {
    fetchWarehouses();
    fetchItems();
  }, []);

  useEffect(() => {
    if (fromWarehouseName?.value) {
      filterItemsByWarehouse(fromWarehouseName.value);
    } else {
      setFilteredItems(items);
    }
  }, [fromWarehouseName, items]);

  const fetchWarehouses = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        buildApiUrl("/api/warehouse/get-warehouse/name"),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setWarehouses(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch warehouses");
    }
  };

  const fetchItems = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        buildApiUrl("/api/warehouse/inventory"),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setItems(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch inventory items");
    }
  };

  const filterItemsByWarehouse = (warehouseName) => {
    const filtered = items.filter(
      (item) => item.warehouseName === warehouseName
    );
    setFilteredItems(filtered);
  };

  const handleItemChange = (index, selectedOption) => {
    const newItems = [...transferItems];
    newItems[index].itemCode = selectedOption?.value || "";

    if (selectedOption) {
      const selectedItem = filteredItems.find(
        (item) => item.itemCode === selectedOption.value
      );
      if (selectedItem) {
        newItems[index].itemName = selectedItem.itemName;
        newItems[index].itemCategory = selectedItem.itemCategory;
        newItems[index].itemImage = selectedItem.itemImage;
        newItems[index].inwardReference = selectedItem.inwardReference;
        newItems[index].inwardShipmentMark = selectedItem.inwardShipmentMark;
      }
    } else {
      newItems[index].itemName = "";
      newItems[index].itemCategory = "";
      newItems[index].itemImage = "";
      newItems[index].inwardReference = "";
      newItems[index].inwardShipmentMark = "NA";
    }

    setTransferItems(newItems);
  };

  const handleQuantityChange = (index, e) => {
    const newItems = [...transferItems];
    newItems[index].transferQuantity = parseInt(e.target.value) || 0;
    setTransferItems(newItems);
  };

  const addItemRow = () => {
    setTransferItems([
      ...transferItems,
      { itemCode: "", itemName: "", transferQuantity: 0 },
    ]);
  };

  const removeItemRow = (index) => {
    const newItems = transferItems.filter((_, i) => i !== index);
    setTransferItems(newItems);
  };

  const handleTransfer = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        buildApiUrl("/api/warehouse/transfer/warehouse1/warehouse2"),
        {
          fromWarehouseName: fromWarehouseName?.value,
          toWarehouseName: toWarehouseName?.value,
          transferItems: transferItems.map((item) => ({
            itemCode: item.itemCode,
            itemName: item.itemName,
            itemCategory: item.itemCategory,
            itemImage: item.itemImage,
            transferQuantity: item.transferQuantity,
            inwardReference: item.inwardReference,
            inwardShipmentMark: item.inwardShipmentMark,
          })),
          vehicleNumber,
          materialCheckedBy,
          driverName,
          driverMobileNumber,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        toast.success("Transfer successful");
        onTransfer();
        onClose();
      }
    } catch (error) {
      toast.error("Transfer failed");
    }
  };

  const warehouseOptions = warehouses.map((warehouse) => ({
    value: warehouse.warehouseName,
    label: warehouse.warehouseName,
  }));

  const itemOptions = filteredItems.map((item) => ({
    value: item.itemCode,
    label: `${item.itemName} (${item.itemCode}) - ${item.inHandStockQuantity} units`,
  }));

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-70 z-50 transition-opacity duration-300">
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl mx-4 p-4 transform transition-all duration-300 scale-100 max-h-[90vh] overflow-y-auto"
        style={{ scrollbarWidth: "thin" }}
      >
        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
          <div className="flex items-center gap-2">
            <FaTruck className="text-indigo-600" size={16} />
            <h2 className="text-lg font-semibold text-gray-800">
              Transfer Inventory
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes size={16} />
          </button>
        </div>

        <div className="mt-3" style={{ scrollbarWidth: "thin" }}>
          {/* Vehicle and Driver Section */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
            <div>
              <label
                htmlFor="vehicleNumber"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Vehicle Number
              </label>
              <input
                id="vehicleNumber"
                type="text"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />
            </div>
            <div>
              <label
                htmlFor="materialCheckedBy"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Material Checked By
              </label>
              <input
                id="materialCheckedBy"
                type="text"
                value={materialCheckedBy}
                onChange={(e) => setMaterialCheckedBy(e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />
            </div>
            <div>
              <label
                htmlFor="driverName"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Driver Name
              </label>
              <input
                id="driverName"
                type="text"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />
            </div>
            <div>
              <label
                htmlFor="driverMobileNumber"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Driver Mobile
              </label>
              <input
                id="driverMobileNumber"
                type="text"
                value={driverMobileNumber}
                onChange={(e) => setDriverMobileNumber(e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />
            </div>
          </div>

          {/* Warehouse Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <div>
              <label
                htmlFor="fromWarehouse"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                From Warehouse
              </label>
              <Select
                id="fromWarehouse"
                value={fromWarehouseName}
                onChange={setFromWarehouseName}
                options={warehouseOptions}
                placeholder="Select Warehouse"
                styles={style}
                menuPortalTarget={document.body}
                className="text-xs"
              />
            </div>
            <div>
              <label
                htmlFor="toWarehouse"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                To Warehouse
              </label>
              <Select
                id="toWarehouse"
                value={toWarehouseName}
                onChange={setToWarehouseName}
                options={warehouseOptions}
                placeholder="Select Warehouse"
                styles={style}
                menuPortalTarget={document.body}
                className="text-xs"
              />
            </div>
          </div>

          <div className="hidden sm:grid grid-cols-[1fr_120px_20px] gap-2 text-xs mb-2 text-gray-700 bg-gray-200 font-medium py-1 rounded-md">
            <span className="pl-2">Item</span>
            <span>Quantity</span>
            <span></span>
          </div>

          {/* Transfer Items */}
          <div className="space-y-2">
            {transferItems.map((item, index) => (
              <div
                key={index}
                className="grid grid-cols-[1fr_20px] items-end sm:items-center sm:grid-cols-[1fr_120px_20px] gap-2 bg-gray-50 p-2 sm:p-0 rounded-lg border sm:border-0 border-gray-300"
              >
                <div className=" col-span-full sm:col-span-1">
                  <label
                    htmlFor={`itemCode-${index}`}
                    className="block text-xs sm:hidden font-medium text-gray-600 mb-1"
                  >
                    Item
                  </label>
                  <Select
                    id={`itemCode-${index}`}
                    value={itemOptions.find(
                      (option) => option.value === item.itemCode
                    )}
                    onChange={(selectedOption) =>
                      handleItemChange(index, selectedOption)
                    }
                    options={itemOptions}
                    placeholder="Select Item"
                    styles={style}
                    menuPortalTarget={document.body}
                    className="text-xs"
                  />
                </div>
                <div className="">
                  <label
                    htmlFor={`quantity-${index}`}
                    className="block text-xs sm:hidden font-medium text-gray-600 mb-1"
                  >
                    Quantity
                  </label>
                  <input
                    id={`quantity-${index}`}
                    type="number"
                    value={item.transferQuantity}
                    onChange={(e) => handleQuantityChange(index, e)}
                    className="w-full px-2 py-[5px] text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    min="1"
                  />
                </div>
                {transferItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItemRow(index)}
                    className=" text-red-500 hover:text-red-700 rounded-md mb-2 sm:mb-0 hover:bg-gray-200 transition-colors"
                  >
                    <HiOutlineTrash size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end items-center pt-3 border-gray-200 gap-2">
          <button
            onClick={addItemRow}
            type="button"
            className="px-3 py-2 bg-blue-100 text-indigo-600 text-xs rounded-md hover:bg-blue-200 transition-colors duration-200 flex items-center gap-1"
          >
            <FaPlus size={12} />
            Add Item
          </button>
          <button
            onClick={onClose}
            type="button"
            className="px-3 py-2 bg-gray-200 text-gray-700 text-xs rounded-md hover:bg-gray-300 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleTransfer}
            type="button"
            className="px-3 py-2 bg-indigo-600 text-white text-xs rounded-md hover:bg-indigo-800 transition-colors duration-200 flex items-center gap-1"
          >
            <FaTruck size={12} />
            Transfer Items
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TransferInventoryModal);
